import sizes from "../../design-tokens/sizes";
import { check, overflow } from "../../js/helpers/built-in-icons";
import { actions, createStore, events, reducers, renderState } from "../../js/state-machine";
import {
    createCSSClass,
    debounce,
    isHTMLElement,
    isObject,
    prefix,
    randomNumGenerator,
} from "../../js/utilities";
import { Pop } from "../pop/pop";
import { alignments } from "../pop/pop-reducer";
import TabItem from "./tab-item";
import TabPane from "./tab-pane";

export const componentName = "Tabs";

export const defaultProps = {
    moreLabel: "More",
    backLabel: "Back",
};

export const eventMap = {
    opened: events.opened(componentName),
    closed: events.closed(componentName),
};

export const tabsClasses = {
    wrapper: `${prefix}tabs`,
    wrapperSm: `${prefix}tabs--sm`,
    wrapperContained: `${prefix}tabs--contained`,
    wrapperMobileStacked: `${prefix}tabs--mobile-stacked`,
    wrapperMobileJustified: `${prefix}tabs--mobile-justified`,
    wrapperIsPressing: `${prefix}tabs--is-pressing`,
    list: `${prefix}tabs__list`,
    listOverflow: `${prefix}tabs__list--overflow`,
    listContainer: `${prefix}tabs__list-container`,
    listContainerHiddenGradient: `${prefix}tabs__list-container--hidden-gradient`,
    tab: `${prefix}tabs__tab`,
    tabLabel: `${prefix}tabs__tab__label`,
    tabWithImage: `${prefix}tabs__tab--with-image`,
    tabHidden: `${prefix}tabs__tab--hidden`,
    more: `${prefix}tabs__more`,
    moreIcon: `${prefix}tabs__more__icon`,
    moreWrapper: `${prefix}tabs__more-wrapper`,
    moreContainer: `${prefix}tabs__more-wrapper__container`,
    moreList: `${prefix}tabs__more-list`,
    moreListItem: `${prefix}tabs__more-list__item`,
    moreListItemHidden: `${prefix}tabs__more-list__item--hidden`,
    pane: `${prefix}tabs__pane`,
    paneContainer: `${prefix}tabs__pane-container`,
    bodyOverflow: `${prefix}tabs--body-overflow`,
};

export const views = {
    tabs: {
        default: { classList: [`!${tabsClasses.wrapperIsPressing}`] },
        isPressing: { classList: [tabsClasses.wrapperIsPressing] },
    },
    listContainer: {
        default: { classList: [`!${tabsClasses.listContainerHiddenGradient}`] },
        hiddenGradient: { classList: [tabsClasses.listContainerHiddenGradient] },
    },
    list: {
        default: { classList: [`!${tabsClasses.listOverflow}`] },
        overflow: { classList: [tabsClasses.listOverflow] },
    },
    tab: {
        default: { classList: [`!${tabsClasses.tabHidden}`] },
        hidden: { classList: [tabsClasses.tabHidden] },
        active: { attrs: { "aria-selected": "true", tabindex: "0" } },
        inactive: { attrs: { "aria-selected": "false", tabindex: "-1" } },
    },
    tabPane: {
        inactive: { attrs: { "aria-hidden": "true", tabindex: "-1" } },
    },
    more: {
        default: { attrs: { "aria-selected": "false", tabindex: "-1" } },
        active: { attrs: { "aria-selected": "true", tabindex: "0" } },
        list: {
            item: {
                default: {
                    classList: [`!${tabsClasses.moreListItemHidden}`],
                    attrs: { "aria-hidden": "false" },
                },
                hidden: {
                    classList: [tabsClasses.moreListItemHidden],
                    attrs: { "aria-hidden": "true" },
                },
                active: { attrs: { "aria-current": "true" } },
                inactive: { attrs: { "aria-current": "false" } },
            },
        },
    },
};

/**
 * Creates Tabs
 * @param {HTMLElement} node
 * @returns Tabs
 */
export default function Tabs(element, props) {
    if (!isHTMLElement(element)) return console.error(`${componentName} missing element`);
    if (element[componentName]) return console.warn(`${componentName} already initialized`);
    if (!isObject(props)) props = {};

    // Merge with default to ensure default config
    props = Object.assign({}, defaultProps, props, element.dataset);

    const checkIconSvg = new DOMParser().parseFromString(check(), "text/html").body.firstChild;
    const moreIconSvg = new DOMParser().parseFromString(overflow(), "text/html").body.firstChild;
    moreIconSvg.classList.add(tabsClasses.moreIcon);

    let tabsListElement = null;
    let tabsListContainerElement = null;
    let tabsPaneContainerElement = null;
    let moreLiElement = null;
    let moreButtonElement = null;
    let moreListElement = null;
    let popInstance = null;
    let popWrapper = null;
    let popTriggerId = null;
    let popContainer = null;
    let tabItemList = [];
    let tabPaneList = [];
    let isScreenBiggerThanMedium = false;
    let isPressing = false;
    let isMobileStacked = false;
    let isMobileJustified = false;
    let isContained = false;
    let hasMore = false;
    let scrollHeightDynamicClass;
    let scrollHeightDynamicClassMemory;

    /**
     * Validate the markup of the vanilla component
     */
    const validateMarkup = () => {
        if (tabItemList.length !== tabPaneList.length) {
            console.error(`${componentName} don't have same number of tabs and tab panels"`);
            return;
        }

        const tabAriaSelected = tabsListElement.querySelectorAll(
            `.${tabsClasses.tab}[aria-selected="true"]`
        ).length;

        if (tabAriaSelected === 0) {
            console.warn(`${componentName} don't have any tab aria-selected="true"`);
        }
        if (tabAriaSelected > 1) {
            console.warn(`${componentName} have more than one tab aria-selected="true"`);
        }

        const paneAriaHidden = tabsPaneContainerElement.querySelectorAll(
            `.${tabsClasses.pane}[aria-hidden="false"]`
        ).length;

        if (paneAriaHidden === 0) {
            console.warn(`${componentName} don't have any pane aria-hidden="false"`);
        }
        if (paneAriaHidden > 1) {
            console.warn(`${componentName} have more than one pane aria-hidden="false"`);
        }

        return true;
    };

    const setScreenSize = () => {
        const windowInnerWidth = window.innerWidth;
        const mediumBreakpoint = parseInt(sizes.breakpoints["breakpoints-md"]()) - 1;
        isScreenBiggerThanMedium = windowInnerWidth >= mediumBreakpoint;
    };

    const initTabs = () => {
        // Create the menu that goes inside the more button
        createMoreMenu();

        tabItemList = tabItemList.map((tabItem, index) => {
            tabItem.setAttribute("aria-posinset", index + 1);
            tabItem.setAttribute("aria-setsize", tabItemList.length);

            return {
                element: tabItem,
                menuElement: createMoreMenuItem(tabItem, index),
                isVisible: true,
                index,
                instance: new TabItem(tabItem, {
                    index,
                    tabsStore,
                    setActiveTab,
                    handleTabKeydown,
                    isMobileStacked,
                    wrapper: element,
                    eventMap,
                }),
            };
        });
    };

    const initTabsPane = () => {
        tabPaneList = tabPaneList.map((tabPaneItem, index) => {
            return {
                element: tabPaneItem,
                instance: new TabPane(tabPaneItem, {
                    index,
                    tabsStore,
                    isMobileStacked,
                    backLabel: props.backLabel,
                }),
            };
        });
    };

    const handleWindowResize = () => {
        setScreenSize();
        startUpdateTabs();
    };

    /**
     * When resizing the screen a class with overflow hidden is added in order to make the calculation
     * of each individual tab without leaking the elements, once the calculations are done that class is removed.
     * Resising the screen trigger the event many times, so a debbounce is added in order to keep performance ok.
     */
    const startUpdateTabs = () => {
        renderState(views.list.overflow, tabsListElement);
        updateTabsDebouncing();
    };

    /**
     * Controls the visibility of tabs when starting the component and also when resizing the screen
     */
    const updateTabs = () => {
        const { width: listAvailableWidth } = tabsListElement.getBoundingClientRect();

        // The initial value is a threshold to handle font loading and 125 for the more button
        let stackedWidth = tabItemList.length * 6 + 125;
        let stackValueExceeded = false;

        const tabStoreState = tabsStore.getState();

        if (isScreenBiggerThanMedium) {
            // Make sure to remove any dynamic class that fix the bottom border in small screens
            renderState(
                {
                    classList: [
                        `!${scrollHeightDynamicClass}`,
                        `!${scrollHeightDynamicClassMemory}`,
                    ],
                },
                tabsListContainerElement
            );

            // Make sure if there aren't active tabs (Returning from a mobile stacked view), turn the first one active
            const activeTabIndex = tabStoreState.reduce((acc, current, index) => {
                if (acc === -1 && current) acc = index;
                return acc;
            }, -1);

            if (activeTabIndex === -1) setActiveTab(0);
            else setActiveTab(activeTabIndex);

            // Run trought the tabs adding stackValueExceeded to see how many tabs fit on listAvailableWidth
            tabItemList.forEach((tabItem) => {
                let isVisible = true;
                const { width: itemWidth } = tabItem.element.getBoundingClientRect();

                if (!stackValueExceeded && stackedWidth + itemWidth < listAvailableWidth) {
                    stackedWidth = stackedWidth + itemWidth;
                    renderState(views.tab.default, tabItem.element);
                    renderState(views.more.list.item.hidden, tabItem.menuElement);
                } else {
                    stackValueExceeded = true;
                    isVisible = false;
                    renderState(views.tab.hidden, tabItem.element);
                    renderState(views.more.list.item.default, tabItem.menuElement);
                }

                tabItem.isVisible = isVisible;
            });

            // If exceeded create more menu and verify if the
            if (stackValueExceeded) {
                hasMore = true;
                createMoreButton();
                appendMoreListToPopContainer();
                appendPopToTabsList();
                setMoreSelectedState(tabStoreState);
            } else if (popInstance) {
                hasMore = false;
                moreLiElement.remove();
            }

            renderState(views.list.default, tabsListElement);
        } else {
            // Those two renderStates need to trigger before toggleMobileGradientOverlay & adjustBottomBorderWithScrollbar
            // So once those two function run, the containe will already have a scrollbar
            renderState(views.list.default, tabsListElement);

            tabItemList.forEach((tabItem) => {
                tabItem.isVisible = true;
                renderState(views.tab.default, tabItem.element);
            });

            // Small screen modifiers
            if (isMobileStacked) {
                tabsStore.dispatch(actions.collapseAll());
            } else if (!isMobileStacked && !isMobileJustified) {
                toggleMobileGradientOverlay();
                adjustBottomBorderWithScrollbar();
            }

            if (popInstance) {
                hasMore = false;
                moreLiElement.remove();
            }
        }
    };

    /**
     * Wrapper the updateTabs into a debounce function
     * Need to be on component scope to keep the reference
     */
    const updateTabsDebouncing = debounce(updateTabs, 100);

    /**
     * Enable/disable the gradient overlay of inline tabs in small screens
     */
    const toggleMobileGradientOverlay = () => {
        const { offsetWidth: lastItemOffsetWidth } = tabItemList[tabItemList.length - 1].element;
        const { offsetWidth, scrollLeft, scrollWidth } = tabsListElement;

        if (scrollLeft + offsetWidth >= scrollWidth - lastItemOffsetWidth / 2) {
            renderState(views.listContainer.hiddenGradient, tabsListContainerElement);
        } else {
            renderState(views.listContainer.default, tabsListContainerElement);
        }
    };

    /**
     * In small screen resolutions looking in a mobile view (Chrome developer) the scrollbar is hidden and
     * becomes a thin overlay scrollbar, when seen outside of that view mode, the default browser scrollbar comes in.
     * As our border bottom is a pseudo element we create a dynamic class do adjust its bottom property
     * to make the border match the tabs and stay in place with any kind of scrollbar.
     */
    const adjustBottomBorderWithScrollbar = () => {
        const scrollbarWidth = parseInt(
            tabsListElement.offsetHeight - tabsListElement.clientHeight
        );
        scrollHeightDynamicClass = `${prefix}dynamic-tabs-${scrollbarWidth}`;

        if (
            createCSSClass(`.${tabsClasses.listContainer}.${scrollHeightDynamicClass}:before`, {
                bottom: `${4 + scrollbarWidth}px`,
            })
        ) {
            renderState(
                {
                    classList: [
                        scrollHeightDynamicClass,
                        scrollHeightDynamicClassMemory !== scrollHeightDynamicClass &&
                            `!${scrollHeightDynamicClassMemory}`,
                    ],
                },
                tabsListContainerElement
            );
        }

        // Save last used class so we can remove it later
        scrollHeightDynamicClassMemory = scrollHeightDynamicClass;
    };

    const createMoreButton = () => {
        if (popInstance) return;

        // Create pop inner list
        moreLiElement = document.createElement("li");
        moreLiElement.setAttribute("role", "none");

        // NOTE: Whenever pop is being created on js side, it could be provided by pop;
        popTriggerId = `dds-tabs-more-${randomNumGenerator()}`;

        popWrapper = document.createElement("div");
        popWrapper.classList.add(tabsClasses.moreWrapper);
        popWrapper.setAttribute("data-trigger", `#${popTriggerId}`);

        popContainer = document.createElement("div");
        popContainer.classList.add(tabsClasses.moreContainer);
        popContainer.setAttribute("tabindex", "-1");
        popContainer.setAttribute("role", "presentation");
        popContainer.setAttribute("aria-hidden", "true");

        moreButtonElement = document.createElement("button");
        const moreButtonSpanElement = document.createElement("span");
        moreButtonSpanElement.innerHTML = props.moreLabel;
        moreButtonElement.setAttribute("type", "button");
        moreButtonElement.setAttribute("role", "tab");
        moreButtonElement.setAttribute("tabindex", "-1");
        moreButtonElement.setAttribute("aria-selected", "false");
        moreButtonElement.appendChild(moreButtonSpanElement);
        moreButtonElement.appendChild(moreIconSvg.cloneNode(true));
        moreButtonElement.setAttribute("id", popTriggerId);
        moreButtonElement.classList.add(tabsClasses.more);

        // Appends
        popWrapper.appendChild(moreButtonElement);
        popWrapper.appendChild(popContainer);
        moreLiElement.appendChild(popWrapper);

        // Start Pop
        popInstance = new Pop(popWrapper, {
            mainClass: tabsClasses.moreWrapper,
            position: actions.types.bottom,
            alignment: alignments.end,
            closeOnBlur: true,
            eventName: componentName,
        });

        // Subscribe to pop visibility changes
        moreButtonElement.addEventListener("keydown", handleTabKeydown);
    };

    const appendPopToTabsList = () => {
        if (!tabsListElement.contains(moreLiElement)) tabsListElement.appendChild(moreLiElement);
    };

    const appendMoreListToPopContainer = () => {
        if (!popContainer.contains(moreListElement)) popContainer.appendChild(moreListElement);
    };

    const createMoreMenu = () => {
        if (moreListElement) return;

        moreListElement = document.createElement("ul");
        moreListElement.setAttribute("role", "menu");
        moreListElement.setAttribute("tabindex", "-1");
        moreListElement.classList.add(tabsClasses.moreList);

        moreListElement.addEventListener("keydown", handleMoreListKeydown);
    };

    const createMoreMenuItem = (item, index) => {
        const itemText = item.querySelector(`.${tabsClasses.tabLabel}`).cloneNode(true);
        itemText.removeAttribute("class");

        const overflowItem = document.createElement("li");
        overflowItem.setAttribute("role", "none");

        const overflowButton = document.createElement("button");
        overflowButton.classList.add(tabsClasses.moreListItem);
        overflowButton.setAttribute("type", "button");
        overflowButton.setAttribute("role", "menuitem");
        overflowButton.setAttribute("tabindex", "-1");
        overflowButton.setAttribute("data-index", index);
        overflowButton.setAttribute("aria-hidden", "false");
        overflowButton.setAttribute("aria-current", item.getAttribute("aria-selected"));
        overflowButton.setAttribute("aria-posinset", index + 1);
        overflowButton.setAttribute("aria-setsize", tabItemList.length);

        overflowButton.appendChild(itemText);
        overflowButton.appendChild(checkIconSvg.cloneNode(true));
        overflowItem.appendChild(overflowButton);
        moreListElement.appendChild(overflowItem);

        // Not sending parameters to handleMoreMenuItemClick so we can removeEventListener on dispose
        overflowButton.addEventListener("click", handleMoreMenuItemClick);

        return overflowButton;
    };

    const setActiveTab = (index) => {
        const action = actions.setActiveIndex(index, true);
        tabsStore.dispatch(action);
    };

    const getLastVisibleTab = () => {
        return tabItemList.reduce((previousValue, currentValue) => {
            return currentValue.isVisible ? currentValue : previousValue;
        });
    };

    const getPrevTab = (index) => {
        if (index === null) {
            const lastVisibleTab = getLastVisibleTab();
            return { element: lastVisibleTab.element, index: lastVisibleTab.index };
        }
        const prevIndex = Math.max(index - 1, 0);
        return { element: tabItemList[prevIndex].element, index: prevIndex };
    };

    const getNextTab = (index) => {
        // Index will be null if the keydown comes from more tab, there is no nextTab in more tab
        if (index === null) return;

        const nextIndex = index + 1;

        if (tabItemList[nextIndex] && tabItemList[nextIndex].isVisible) {
            return { element: tabItemList[nextIndex].element, index: nextIndex };
        }
        if (hasMore) {
            return { element: moreButtonElement, index: null };
        }
        return { element: tabItemList[index].element, index };
    };

    /**
     * Set the pressing state to control focus when using all the needed keyboard keys
     * Also append a window event listener for mouse down, once mouse down happen we unsetUserIsPressing that means
     * user is not using keyboard anymore
     */
    const setUserIsPressing = () => {
        isPressing = true;
        renderState(views.tabs.isPressing, element);
        window.addEventListener("mousedown", unsetUserIsPressing);
    };

    /**
     * Unset the pressing state and remove event listener added to window
     */
    const unsetUserIsPressing = () => {
        isPressing = false;
        renderState(views.tabs.default, element);
        window.removeEventListener("mousedown", unsetUserIsPressing);
    };

    /**
     * Handle the tabs and more menu keydown on stacked version (horizontal axis)
     */
    const handleInlineTabKeydown = (e, index = null) => {
        switch (e.keyCode) {
            // Esc (More button)
            case 27:
                if (!popInstance) return;
                if (index === null) {
                    popInstance.close();
                    moreButtonElement.focus();
                }
                break;
            // End key
            case 35:
                e.preventDefault();
                if (hasMore) {
                    moreButtonElement.focus();
                } else {
                    tabItemList[tabItemList.length - 1].element.focus();
                    setActiveTab(tabItemList.length - 1);
                }
                break;
            // Home key
            case 36:
                e.preventDefault();
                tabItemList[0].element.focus();
                setActiveTab(0);
                break;
            // Left arrow key
            case 37: {
                const prevTab = getPrevTab(index);
                prevTab && prevTab.element && prevTab.element.focus();
                setActiveTab(prevTab.index);
                break;
            }
            // Right arrow key
            case 39: {
                const nextTab = getNextTab(index);
                nextTab && nextTab.element && nextTab.element.focus();
                // Index comes null when nextTab is the more tab, don't need to set as active
                if (nextTab && nextTab.index !== null) setActiveTab(nextTab.index);
                break;
            }
            // Arrow up (More button)
            // Arrow down (More button)
            // NOTE: Once a screen reader is available arrow down will not open the menu
            // thats expected since arrow down is used to read next line
            // Arrow down and up works on determinated items base on their role, like role="menu|menuitem|etc"
            case 38:
            case 40: {
                // Prevent scrolling
                e.preventDefault();
                if (!popInstance) return;
                const isOpened = popInstance.visibility.getState();
                if (index === null && !isOpened) {
                    popInstance.open();
                } else if (index === null && isOpened) {
                    const firstMoreItem = getNextMoreItem();
                    firstMoreItem && firstMoreItem.focus();
                }
                break;
            }
        }
    };

    /**
     * Handle the tabs keydown on stacked version (vertical axis)
     */
    const handleStackedTabKeydown = (e, index = null) => {
        // The prevent default need to be in each key, so when you enter it trigger the click of the button instead of preventing
        switch (e.keyCode) {
            // End key
            case 35:
                e.preventDefault();
                tabItemList[tabItemList.length - 1].element.focus();
                break;
            // Home key
            case 36:
                e.preventDefault();
                tabItemList[0].element.focus();
                break;
            // Arrow down key
            case 38: {
                e.preventDefault();
                const prevTab = getPrevTab(index);
                prevTab && prevTab.element && prevTab.element.focus();
                break;
            }
            // Arrow up key
            case 40: {
                e.preventDefault();
                const nextTab = getNextTab(index);
                nextTab && nextTab.element && nextTab.element.focus();
                break;
            }
        }
    };

    /**
     * Event function passed to tab childrens and more menu to control keyboard
     */
    const handleTabKeydown = (e, index = null) => {
        if (!isPressing && [27, 35, 36, 37, 39, 38, 40].includes(e.keyCode)) setUserIsPressing();

        if (isScreenBiggerThanMedium || !isMobileStacked) {
            handleInlineTabKeydown(e, index);
        } else if (!isScreenBiggerThanMedium && isMobileStacked) {
            handleStackedTabKeydown(e, index);
        }
    };

    const handleMoreMenuItemClick = (e) => {
        const index = parseInt(e.target.getAttribute("data-index"));
        setActiveTab(index);
    };

    const getFirstMoreItem = () => {
        return tabItemList
            .filter((item) => {
                return !item.isVisible;
            })
            .shift();
    };

    const getFocusedMoreItem = () => {
        return tabItemList.reduce((acc, current, index) => {
            if (acc === -1 && current.menuElement === document.activeElement) acc = index;
            return acc;
        }, -1);
    };

    const getPrevMoreItem = () => {
        if (document.activeElement === moreButtonElement) {
            const firstMoreItem = getFirstMoreItem();
            return firstMoreItem.menuElement;
        }

        const focusedIndex = getFocusedMoreItem();
        const prevItem = focusedIndex - 1;

        if (tabItemList[prevItem] && !tabItemList[prevItem].isVisible) {
            return tabItemList[prevItem].menuElement;
        }
        return;
    };

    const getNextMoreItem = () => {
        if (document.activeElement === moreButtonElement) {
            const firstMoreItem = getFirstMoreItem();
            return firstMoreItem.menuElement;
        }

        const focusedIndex = getFocusedMoreItem();
        const nextIndex = focusedIndex + 1;

        if (tabItemList[nextIndex] && !tabItemList[nextIndex].isVisible) {
            return tabItemList[nextIndex].menuElement;
        }
        return;
    };

    const handleMoreListKeydown = (e) => {
        if (!isPressing && [27, 38, 40].includes(e.keyCode)) setUserIsPressing();
        switch (e.keyCode) {
            // Esc (More button)
            case 27:
                popInstance.close();
                moreButtonElement.focus();
                break;
            // Arrow up
            case 38: {
                e.preventDefault();
                const prevItem = getPrevMoreItem();
                prevItem && prevItem.focus();
                break;
            }
            // Arrow down
            case 40: {
                e.preventDefault();
                const nextItem = getNextMoreItem();
                nextItem && nextItem.focus();
                break;
            }
        }
    };

    /**
     * Verify if the new active tab is inside of the more menu to give it the active state
     */
    const setMoreSelectedState = (tabStoreState) => {
        let isActiveInsideMore = false;

        tabItemList.forEach((item, index) => {
            if (isActiveInsideMore) return;
            if (!item.isVisible && tabStoreState[index]) isActiveInsideMore = true;
        });

        renderState(isActiveInsideMore ? views.more.active : views.more.default, moreButtonElement);
    };

    /**
     * Whenever the list state changes watch the more menu items to activate/deactivate
     */
    const handleStateChange = (newStateList) => {
        newStateList.forEach((state, index) => {
            renderState(
                state ? views.more.list.item.active : views.more.list.item.inactive,
                tabItemList[index].menuElement
            );
        });

        setMoreSelectedState(newStateList);
    };

    /**
     * Disposing the elements from the reducer
     */
    const dispose = () => {
        element[componentName] = null;
        element = null;

        tabItemList.forEach((item) => {
            item.instance.dispose();
        });

        tabPaneList.forEach((content) => {
            content.instance.dispose();
        });

        tabItemList = [];
        tabPaneList = [];

        if (popInstance) {
            popInstance.dispose();

            hasMore = false;
            moreLiElement.remove();
        }

        window.removeEventListener("resize", handleWindowResize);
        window.removeEventListener("mousedown", unsetUserIsPressing);
        if (moreListElement) moreListElement.removeEventListener("keydown", handleMoreListKeydown);
        if (moreButtonElement) moreButtonElement.removeEventListener("keydown", handleTabKeydown);
    };

    tabsListElement = element.querySelector(`.${tabsClasses.list}`);
    tabsListContainerElement = element.querySelector(`.${tabsClasses.listContainer}`);
    tabsPaneContainerElement = element.querySelector(`.${tabsClasses.paneContainer}`);
    tabItemList = Array.from(element.querySelectorAll(`.${tabsClasses.tab}`));
    tabPaneList = Array.from(element.querySelectorAll(`.${tabsClasses.pane}`));
    isContained = element.classList.contains(`${tabsClasses.wrapperContained}`);
    isMobileStacked =
        isContained || element.classList.contains(`${tabsClasses.wrapperMobileStacked}`);
    isMobileJustified = element.classList.contains(`${tabsClasses.wrapperMobileJustified}`);

    if (!validateMarkup()) return false;

    const initialState = tabItemList.map((item) => {
        return item.getAttribute("aria-selected") === "true";
    });
    const tabsStore = createStore(reducers.listReducer, initialState);
    tabsStore.subscribe(handleStateChange);

    setScreenSize();
    initTabs();
    initTabsPane();
    updateTabs();

    window.addEventListener("resize", handleWindowResize);
    if (!isMobileStacked && !isMobileJustified)
        tabsListElement.addEventListener("scroll", toggleMobileGradientOverlay);

    // Api
    const api = {
        element,
        setActiveTab,
        dispose,
    };

    element[componentName] = api;

    return api;
}
